/**
 * Farm system: fields slowly replenish (regen, clamped to max) and enforce a
 * simultaneous-worker cap. When a field is over-staffed, the excess gatherers
 * (highest eids, for determinism) are bumped off and sent idle rather than
 * force-walked back to a node they can't work.
 */
import type { World } from "../world";
import type { Registry } from "../../data/registry";
import type { Eid } from "../types";
import { FIXED_DT } from "../types";
import { countGatherers } from "../economy";

/** Nearest finished drop-site building owned by `player`, or null. Local to the
 *  farm system so a bumped-off farmer can bank its partial load without reaching
 *  for the untyped world helper. */
function nearestDropSite(world: World, player: number, x: number, y: number): Eid | null {
  const set = world.dropsitesByPlayer.get(player);
  if (!set) return null;
  let best: Eid | null = null;
  let bestD = Infinity;
  for (const eid of set) {
    const tr = world.transform.get(eid);
    const b = world.building.get(eid);
    if (!tr || !b || b.isFoundation) continue;
    const d = (tr.pos.x - x) ** 2 + (tr.pos.y - y) ** 2;
    if (d < bestD || (d === bestD && eid < (best ?? Infinity))) { bestD = d; best = eid; }
  }
  return best;
}

export function farmSystem(world: World, _reg: Registry): void {
  // REGROW: fields with a regen rate slowly replenish, clamped to max.
  for (const [, r] of world.resource) {
    if (r.regen === undefined) continue;
    r.amount = Math.min(r.max ?? r.amount, r.amount + r.regen * FIXED_DT);
  }

  // ENFORCE CAP: a capped field never exceeds its worker cap. Keep the first
  // `cap` gatherers (ascending eid), bump the rest off the node into idle.
  for (const [eid, r] of world.resource) {
    if (r.cap == null) continue;
    if (countGatherers(world, eid) <= r.cap) continue;

    const workers: number[] = [];
    for (const [wid, g] of world.gatherer) {
      if (g.node === eid) workers.push(wid);
    }
    workers.sort((a, b) => a - b);

    for (let i = r.cap; i < workers.length; i++) {
      const wid = workers[i];
      const g = world.gatherer.get(wid);
      if (!g) continue;
      g.node = null;
      const mv = world.move.get(wid);
      if (!mv) continue;
      // Carrying a partial load? Send it to bank at the nearest drop-site, then
      // idle — don't strand the carry by dropping straight to idle.
      const tr = world.transform.get(wid);
      const owner = world.owner.get(wid)?.player;
      if (g.carrying > 1e-6 && tr && owner !== undefined) {
        const ds = nearestDropSite(world, owner, tr.pos.x, tr.pos.y);
        if (ds !== null) {
          g.state = "toDropoff";
          g.dropSite = ds;
          mv.intent = { kind: "dropoff", dropSite: ds };
          mv.path = [];
          continue;
        }
      }
      if (mv.intent?.kind === "gather") {
        mv.intent = undefined;
        mv.path = [];
      }
    }
  }
}
